import React from 'react';
import { ExternalLink } from 'lucide-react';

interface DataFieldProps {
  label: string;
  value: string | number | boolean | undefined | null;
  type?: 'text' | 'link' | 'boolean' | 'phone' | 'email';
}

export const DataField: React.FC<DataFieldProps> = ({ label, value, type = 'text' }) => {
  const displayValue = value === undefined || value === null || value === '' ? '-' : String(value);

  // Helper function to truncate long URLs for display
  const truncateUrl = (url: string, maxLength: number = 40): string => {
    if (url.length <= maxLength) return url;
    // Remove protocol for cleaner display
    const withoutProtocol = url.replace(/^https?:\/\//, '');
    if (withoutProtocol.length <= maxLength) return withoutProtocol;
    return withoutProtocol.substring(0, maxLength - 3) + '...';
  };

  const renderValue = () => {
    if (displayValue === '-') return <span className="text-gray-400 dark:text-gray-500">-</span>;

    switch (type) {
      case 'link':
        return (
          <a
            href={displayValue.startsWith('http') ? displayValue : `https://${displayValue}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-yellow-600 underline hover:text-yellow-700 flex items-center gap-1"
            title={displayValue}
          >
            {truncateUrl(displayValue)}
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
          </a>
        );
      case 'boolean':
        return value ? (
          <span className="text-green-600 dark:text-green-400">Yes</span>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">No</span>
        );
      case 'email':
        return (
          <a
            href={`mailto:${displayValue}`}
            className="text-yellow-600 underline hover:text-yellow-700"
          >
            {displayValue}
          </a>
        );
      case 'phone':
        return (
          <a
            href={`tel:${displayValue}`}
            className="text-yellow-600 underline hover:text-yellow-700"
          >
            {displayValue}
          </a>
        );
      default:
        return <span className="text-gray-900 dark:text-gray-100">{displayValue}</span>;
    }
  };

  return (
    <div className="py-2">
      <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</dt>
      <dd className="mt-1 text-sm">{renderValue()}</dd>
    </div>
  );
};
